import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  fetchChannels,
  fetchDirectMessages,
  getOrCreateDirectMessage,
} from "../../store/slices/channelSlice";
import { setCurrentChannel } from "../../store/slices/chatSlice";
import { fetchUsers } from "../../store/slices/userSlice";
import { updateUserStatus, updatePreferredLanguage } from "../../store/slices/authSlice";
import { CHAT_LANGUAGES, chatLanguageSymbol, normalizeChatLanguage } from "../../constants/chatLanguages";
import { setChannelSoundPreference } from "../../store/slices/uiSlice";
import { fetchTasks, TaskStatus } from "../../store/slices/taskSlice";
import CreateChannelModal from "./CreateChannelModal";
import StartDirectMessageModal from "./StartDirectMessageModal";
import UserProfileModal from "../common/UserProfileModal";
import MorningDigestOpenButton from "../common/MorningDigestOpenButton";
import CreateMessageTemplateModal from "../templates/CreateMessageTemplateModal";
import CaptionSettingsModal from "../common/CaptionSettingsModal";
import PresignedAvatar from "../common/PresignedAvatar";
import { resolveUsersByIds } from "../../realm/offlineLookups";
import { readLatestActivityByChannelFromRealm, realmAvailable } from "../../realm";
import {
  dedupeDirectMessages,
  extractEntityId,
  findUserForDm,
  isWorkloopDm,
  mergePeopleForLookup,
  normalizeMemberIds,
  userDisplayName,
} from "../../utils/directMessageUtils";
import { workloopInboxInitial, workloopInboxLabel } from "../../utils/organizationBrand";
import { isNotificationsChannel, isTaskCommentChannel, isHiddenForGlobalOrgUser, isVisibleSidebarChannel } from "../../utils/channelTypeUtils";
import { formatPresenceLabel, presenceDotClass, resolveWorkLocation } from "../../utils/presenceLabelUtils";
import { setSidebarIntent } from "../../utils/sidebarIntent";
import {
  SunIcon,
  MoonIcon,
  CloseIcon,
  MenuIcon,
  LogoutIcon,
  ShieldIcon,
  MegaphoneIcon,
  CreateNoteIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  PlusIcon,
  VolumeIcon,
  VolumeMutedIcon,
  ClockIcon,
  BookmarkIcon,
  CalendarIcon,
  EventsIcon,
  NotesIcon,
  TravelIcon,
  PluginsIcon,
  LiveTrackingIcon,
} from "./SidebarIcons";

const STATUS_LABELS = {
  ONLINE: 'Online',
  AVAILABLE: 'Available',
  AWAY: 'Away',
  LONG_AWAY: 'Long Away',
  OFFLINE: 'Offline',
  LOGGED_OUT: 'Logged Out',
  ON_LEAVE: 'Out of Office',
  BUSY: 'Busy',
  DO_NOT_DISTURB: 'Do not Disturb',
  SICK_LEAVE: 'Sick Leave',
  HOLIDAY: 'Holiday',
  HAVING_FOOD: 'Having Food',
  IN_A_CALL: 'In a call',
};

const formatStatusLabel = (user) => formatPresenceLabel(user);

const Sidebar = ({
  currentChannel,
  onChannelSelect,
  onLogout,
  onToggleSidebar,
  onToggleDarkMode,
  sidebarOpen,
  darkMode,
  onDailyTasksToggle,
  dailyTasksOpen,
  onCalendarToggle,
  calendarOpen,
  onEventsToggle,
  eventsOpen,
  onNotesToggle,
  notesOpen,
  onTravelToggle,
  travelOpen,
  onLiveTrackingToggle,
  liveTrackingOpen,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const taskPanelOpen = location.pathname.startsWith("/chat/tasks");
  const onChatShell = location.pathname === "/chat" || location.pathname.startsWith("/chat/");
  const goChatWithIntent = (intent, localHandler) => {
    if (onChatShell && typeof localHandler === "function") {
      localHandler();
      return;
    }
    setSidebarIntent(intent);
    if (intent === "tasks") {
      navigate("/chat/tasks");
      return;
    }
    navigate("/chat");
  };
  const { user } = useSelector((state) => state.auth);
  const {
    channels = [],
    directMessages = [],
    loading,
  } = useSelector((state) => state.channels);
  const { users = [] } = useSelector((state) => state.users);
  const { unreadCounts, messages, lastActivityByChannelId = {} } = useSelector((state) => state.chat);
  const { soundPreferences } = useSelector((state) => state.ui);
  const { tasks = [] } = useSelector((state) => state.tasks);

  const isAdmin = user?.role === "ADMIN" || user?.role === "ORG_ADMIN" || Boolean(user?.canAssignRoles);
  const isSuperAdmin = user?.role === "ADMIN";

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isCaptionSettingsOpen, setIsCaptionSettingsOpen] = useState(false);
  const [isStartDMModalOpen, setIsStartDMModalOpen] = useState(false);
  const [dmSearchQuery, setDmSearchQuery] = useState("");
  const [channelSearchQuery, setChannelSearchQuery] = useState("");
  const [dmUserDetails, setDmUserDetails] = useState([]);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [channelsExpanded, setChannelsExpanded] = useState(true);
  const [directMessagesExpanded, setDirectMessagesExpanded] = useState(true);
  const [openingSelfChat, setOpeningSelfChat] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [languageSearchQuery, setLanguageSearchQuery] = useState("");
  const languageMenuRef = useRef(null);
  const languageSearchInputRef = useRef(null);
  const [prefetchedActivityTimestamps, setPrefetchedActivityTimestamps] = useState({});
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  useEffect(() => {
    if (!showLanguageMenu) {
      setLanguageSearchQuery("");
      return undefined;
    }
    const timer = setTimeout(() => languageSearchInputRef.current?.focus(), 0);
    const handleClickOutside = (event) => {
      if (languageMenuRef.current?.contains(event.target)) return;
      setShowLanguageMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLanguageMenu]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    setIsOnline(typeof navigator !== "undefined" ? navigator.onLine : true);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const languageQuery = languageSearchQuery.trim().toLowerCase();
  const languageOptions = languageQuery
    ? CHAT_LANGUAGES.filter((item) =>
      item.label.toLowerCase().includes(languageQuery)
      || item.symbol.toLowerCase().includes(languageQuery)
      || item.code.toLowerCase().includes(languageQuery)
    )
    : CHAT_LANGUAGES;

  // Handle mute/unmute toggle
  const handleToggleSound = (channelId, e) => {
    e.stopPropagation();
    const currentPreference = soundPreferences[channelId] !== undefined ? soundPreferences[channelId] : true;
    const newPreference = !currentPreference;
    dispatch(setChannelSoundPreference({ channelId, enabled: newPreference }));
  };

  useEffect(() => {
    dispatch(fetchChannels());
    dispatch(fetchTasks());
    dispatch(fetchDirectMessages());
    dispatch(fetchUsers());
  }, [dispatch]);

  // Preload last activity timestamp for sorting
  useEffect(() => {
    const fetchLastActivities = async () => {
      try {
        const allIds = [...new Set([
          ...(channels || []).map((c) => c?.id).filter(Boolean),
          ...(directMessages || []).map((c) => c?.id).filter(Boolean),
        ])];

        if (allIds.length === 0) return;

        const existing = prefetchedActivityTimestamps || {};
        const fromRedux = {};
        allIds.forEach((channelId) => {
          const channelMessages = messages?.[channelId] || [];
          const latest = channelMessages.reduce((maxTs, msg) => {
            const ts = new Date(msg?.createdAt || msg?.timestamp || 0).getTime();
            return ts > maxTs ? ts : maxTs;
          }, existing[channelId] || 0);
          if (latest > 0) fromRedux[channelId] = latest;
        });

        const idsMissingRealm = allIds.filter((id) => (fromRedux[id] || existing[id] || 0) === 0);
        let fromRealm = {};
        if (idsMissingRealm.length > 0 && await realmAvailable()) {
          fromRealm = (await readLatestActivityByChannelFromRealm(idsMissingRealm)) || {};
        }

        const updates = {};
        allIds.forEach((channelId) => {
          const nextTs = Math.max(
            fromRealm[channelId] || 0,
            fromRedux[channelId] || 0,
            existing[channelId] || 0,
          );
          if (nextTs > (existing[channelId] || 0)) updates[channelId] = nextTs;
        });
        if (Object.keys(updates).length === 0) return;
        setPrefetchedActivityTimestamps((prev) => ({ ...prev, ...updates }));
      } catch (error) {
        console.error("Failed to prefetch chat activities:", error);
      }
    };

    fetchLastActivities();
  }, [channels, directMessages, messages]);

  useEffect(() => {
    const fetchDMUserDetails = async () => {
      if (directMessages.length === 0) {
        setDmUserDetails([]);
        return;
      }

      const allMemberIds = [...new Set(
        directMessages.flatMap((dm) => normalizeMemberIds(dm.memberIds))
      )];
      if (allMemberIds.length === 0) {
        setDmUserDetails([]);
        return;
      }

      const resolved = await resolveUsersByIds(allMemberIds, users);
      setDmUserDetails(resolved);
    };

    fetchDMUserDetails();
  }, [directMessages, user?.id, users]);

  useEffect(() => {
    if (dmUserDetails.length === 0 || users.length === 0) return;
    setDmUserDetails((prev) =>
      prev.map((dmUser) => {
        const updated = users.find((u) => extractEntityId(u) === extractEntityId(dmUser));
        if (updated && (
          updated.status !== dmUser.status
          || updated.statusMessage !== dmUser.statusMessage
          || updated.statusSource !== dmUser.statusSource
          || updated.onBreak !== dmUser.onBreak
        )) {
          return {
            ...dmUser,
            status: updated.status,
            statusMessage: updated.statusMessage,
            statusSource: updated.statusSource,
            onBreak: updated.onBreak,
          };
        }
        return dmUser;
      })
    );
  }, [users]);

  const handleChannelClick = (channel) => {
    onChannelSelect(channel);
  };

  const getLastActivityTimestamp = (channel) => {
    if (!channel?.id) return 0;
    const channelId = channel.id;
    let latest = 0;
    const consider = (value) => {
      if (value == null || value === '') return;
      const parsed = typeof value === 'number' ? value : new Date(value).getTime();
      if (!Number.isNaN(parsed) && parsed > latest) latest = parsed;
    };

    consider(channel.lastMessageAt);
    consider(channel.lastMessage?.createdAt);
    consider(channel.lastMessage?.timestamp);
    consider(channel.lastMessage?.updatedAt);
    consider(lastActivityByChannelId[channelId]);

    const channelMessages = messages[channelId];
    if (Array.isArray(channelMessages)) {
      channelMessages.forEach((msg) => {
        consider(msg?.createdAt || msg?.timestamp || msg?.updatedAt);
      });
    }

    consider(prefetchedActivityTimestamps[channelId]);
    consider(channel.updatedAt);
    consider(channel.createdAt);
    return latest;
  };

  const getSortedChannels = (channelsList) => {
    const specialRank = (channel) => {
      if (channel?.type === 'ATTENDANCE' || channel?.systemChannelKey === 'ATTENDANCE' || channel?.name === 'Attendance') return 0;
      if (isNotificationsChannel(channel) || channel?.name === 'Notifications') return 1;
      if (channel?.type === 'ANNOUNCEMENT' || channel?.name === 'Announcements') return 2;
      return 3;
    };
    return [...channelsList].sort((a, b) => {
      const rankDiff = specialRank(a) - specialRank(b);
      if (rankDiff !== 0) return rankDiff;
      const timestampA = getLastActivityTimestamp(a);
      const timestampB = getLastActivityTimestamp(b);
      if (timestampA !== timestampB) return timestampB - timestampA;
      return (a.name || "").localeCompare(b.name || "");
    });
  };

  const peopleForDms = mergePeopleForLookup(dmUserDetails, users);

  const getSortedDirectMessages = (dmList) => {
    const nameOf = (dm) => {
      if (isWorkloopDm(dm)) return workloopInboxLabel(user, dm);
      const otherUser = findUserForDm(dm, user?.id, peopleForDms);
      return (userDisplayName(otherUser) || dm.name || '').trim();
    };
    return [...dmList].sort((a, b) => {
      const workloopA = isWorkloopDm(a) ? 1 : 0;
      const workloopB = isWorkloopDm(b) ? 1 : 0;
      if (workloopA !== workloopB) return workloopA - workloopB;
      const timestampA = getLastActivityTimestamp(a);
      const timestampB = getLastActivityTimestamp(b);
      if (timestampA !== timestampB) return timestampB - timestampA;
      return nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: 'base' });
    });
  };

  const visibleDirectMessages = getSortedDirectMessages(
    dedupeDirectMessages(directMessages, user?.id, peopleForDms)
  );

  const sidebarItems = [
    {
      key: "admin",
      visible: isAdmin,
      onClick: () => navigate("/admin"),
      icon: <ShieldIcon className="w-5 h-5" />,
      title: "Admin Panel",
    },
    {
      key: "announcements",
      visible: user?.hasAnnouncementAccess && !isSuperAdmin,
      onClick: () => navigate("/announcements"),
      icon: <MegaphoneIcon className="w-5 h-5" />,
      title: "Announcements",
    },
    {
      key: "tasks",
      visible: !isSuperAdmin,
      onClick: () => {
        if (onChatShell && location.pathname.startsWith("/chat/tasks")) {
          navigate("/chat");
          return;
        }
        goChatWithIntent("tasks", () => navigate("/chat/tasks"));
      },
      icon: <CreateNoteIcon className="w-5 h-5" />,
      title: "Task Management",
      active: taskPanelOpen,
    },
    {
      key: "caption-settings",
      visible: !isSuperAdmin,
      onClick: () => setIsCaptionSettingsOpen(true),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
      ),
      title: "Caption settings",
      active: isCaptionSettingsOpen,
    },
    {
      key: "templates",
      visible: !isSuperAdmin,
      onClick: () => setIsTemplateModalOpen(true),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6M7 4h10a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 012-2z" />
        </svg>
      ),
      title: "Create Template",
      active: isTemplateModalOpen,
    },
    {
      key: "saved-messages",
      visible: !isSuperAdmin,
      onClick: () => navigate("/saved-messages"),
      icon: <BookmarkIcon className="w-5 h-5" />,
      title: "Bookmarks",
      active: location.pathname === "/saved-messages",
    },
    {
      key: "plugins",
      visible: !isSuperAdmin,
      onClick: () => navigate("/integrations"),
      icon: <PluginsIcon className="w-5 h-5" />,
      title: "Plugins",
      active: location.pathname === "/integrations" || location.pathname === "/cumin" || location.pathname === "/jira",
    },
    {
      key: "live-tracking",
      visible: !isSuperAdmin,
      onClick: () => goChatWithIntent("live-tracking", onLiveTrackingToggle),
      icon: <LiveTrackingIcon className="w-5 h-5" />,
      title: "Live Tracking Tasks",
      active: liveTrackingOpen,
    },
    {
      key: "daily-tasks",
      visible: !isSuperAdmin,
      onClick: () => goChatWithIntent("daily-tasks", onDailyTasksToggle),
      icon: <ClockIcon className="w-5 h-5" />,
      title: "Daily Tasks",
      active: dailyTasksOpen,
    },
    {
      key: "calendar",
      visible: !isSuperAdmin,
      onClick: () => goChatWithIntent("calendar", onCalendarToggle),
      icon: <CalendarIcon className="w-5 h-5" />,
      title: "Calendar",
      active: calendarOpen,
    },
    {
      key: "events",
      visible: !isSuperAdmin,
      onClick: () => goChatWithIntent("events", onEventsToggle),
      icon: <EventsIcon className="w-5 h-5" />,
      title: "Events",
      active: eventsOpen,
    },
    {
      key: "notes",
      visible: !isSuperAdmin,
      onClick: () => goChatWithIntent("notes", onNotesToggle),
      icon: <NotesIcon className="w-5 h-5" />,
      title: "Notes",
      active: notesOpen,
    },
    {
      key: "travel",
      visible: !isSuperAdmin,
      onClick: () => goChatWithIntent("travel", onTravelToggle),
      icon: <TravelIcon className="w-5 h-5" />,
      title: "Travel",
      active: travelOpen,
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-row h-full overflow-hidden">
      {/* Left Sub-Panel: Navigation Icons */}
      <div className="w-16 flex-shrink-0 bg-gray-50 dark:bg-gray-900/50 border-r border-gray-200 dark:border-gray-700 flex flex-col items-center py-4 space-y-4">
        {/* App Logo */}
        <div className="mb-2">
          <img
            src={`${process.env.PUBLIC_URL || ""}/images/favicon.png`}
            alt="WorkLoop"
            className="w-8 h-8 object-contain"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>

        {/* Feature Icons */}
        <div className="flex-1 w-full flex flex-col items-center space-y-2 overflow-y-auto no-scrollbar pt-1">
          {sidebarItems.map(
            ({ key, visible, onClick, icon, title, active }) =>
              visible && (
                <button
                  key={key}
                  onClick={onClick}
                  title={title}
                  className={`relative flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 group ${
                    active
                      ? "bg-primary-500 text-white shadow-lg shadow-primary-500/30"
                      : "text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 hover:text-primary-500"
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    {icon}
                  </span>
                  
                  {/* Tooltip */}
                  <div className="absolute left-14 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 shadow-xl">
                    {title}
                  </div>

                  {/* Red Dot for Task Management */}
                  {key === "tasks" && (() => {
                    const pendingCount = tasks.filter(task => {
                      if (task.status !== TaskStatus.PENDING || task.mapTaskId) return false;
                      const ids = Array.isArray(task.delegateId) ? task.delegateId : (task.delegateId ? [task.delegateId] : []);
                      const isCreator = task.createdByUserId === user?.id;
                      const isDelegate = ids.includes(user?.id);
                      const delegatedToOthers = ids.filter(id => id !== user?.id);
                      
                      // Show if I'm creator and not delegated to others, OR if I'm a delegate and not creator
                      return (isCreator && delegatedToOthers.length === 0) || (isDelegate && !isCreator);
                    }).length;
                    return pendingCount > 0 ? (
                      <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-gray-50 dark:ring-gray-900">
                        {pendingCount}
                      </span>
                    ) : null;
                  })()}
                </button>
              )
          )}
        </div>

        {/* Bottom Actions */}
        <div className="mt-auto flex flex-col items-center space-y-4 pb-2">
          <button
            onClick={onToggleDarkMode}
            className="p-2 rounded-lg hover:bg-white dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
            title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {darkMode ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
          </button>

          <span
            role="status"
            aria-label={isOnline ? "You are online" : "You are offline"}
            title={isOnline ? "You are online" : "You are offline"}
            className={`relative w-3.5 h-3.5 rounded-full transition-all duration-300 ${
              isOnline
                ? "bg-green-400 shadow-[0_0_8px_3px_rgba(74,222,128,0.7)]"
                : "bg-gray-300 dark:bg-gray-500"
            }`}
          >
            <span
              className={`absolute inset-0 m-auto w-1.5 h-1.5 rounded-full bg-white ${
                isOnline ? "opacity-70" : "opacity-25"
              }`}
            />
          </span>
          
          <button
            onClick={onToggleSidebar}
            className="p-2 rounded-lg hover:bg-white dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors hidden lg:block"
            title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            <MenuIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Sidebar Area: Channels, DMs, User Info */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarOpen ? 'w-auto opacity-100' : 'w-0 opacity-0 overflow-hidden invisible lg:visible'}`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">
              WorkLoop
            </h1>
            <button
              onClick={onToggleSidebar}
              className="lg:hidden p-2 -mr-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
              aria-label="Close sidebar"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <PresignedAvatar
                fileKey={user?.profilePicture}
                cacheKey={user?.username}
                name={`${user?.firstName || ""} ${user?.lastName || ""}`}
                initials={`${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`}
                className="w-10 h-10 rounded-full object-cover"
                fallbackClassName="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center text-white text-sm font-medium"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 min-w-0">
                <button
                  onClick={() => navigate("/profile")}
                  className="flex-1 min-w-0 text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-1 -ml-2 transition-colors"
                >
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                </button>
                <MorningDigestOpenButton />
                <button
                  type="button"
                  onClick={onLogout}
                  className="flex-shrink-0 p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Logout"
                >
                  <LogoutIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="mt-0.5 -ml-1 flex items-center justify-between gap-2 min-w-0">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowStatusModal(true); }}
                  className="group flex items-center gap-1 min-w-0"
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${presenceDotClass(user)}`} />
                  <span className="w-3 h-3 flex-shrink-0 flex items-center justify-center text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300">
                    <ChevronDownIcon />
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 truncate min-w-0">
                    {formatStatusLabel(user)}
                  </span>
                </button>
                <div ref={languageMenuRef} className="relative ml-auto flex-shrink-0">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLanguageMenu((open) => !open);
                    }}
                    className="inline-flex items-center gap-1 rounded-md px-0.5 py-0.5 text-[13px] leading-tight text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                    title="Chat language"
                    aria-label="Chat language"
                    aria-expanded={showLanguageMenu}
                    aria-haspopup="listbox"
                  >
                    <span className="font-semibold tracking-wide">
                      {chatLanguageSymbol(user?.preferredLanguage)}
                    </span>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M4 20h4.586a1 1 0 00.707-.293l9.414-9.414a2 2 0 000-2.828l-2.172-2.172a2 2 0 00-2.828 0L4.293 14.707A1 1 0 004 15.414V20z" />
                    </svg>
                  </button>
                  {showLanguageMenu ? (
                    <div
                      className="absolute right-0 z-50 mt-1 w-52 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg"
                      role="listbox"
                    >
                      <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                        <input
                          ref={languageSearchInputRef}
                          type="text"
                          value={languageSearchQuery}
                          onChange={(e) => setLanguageSearchQuery(e.target.value)}
                          placeholder="Search language..."
                          className="w-full h-7 px-2 text-xs rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500/50"
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto py-1">
                        {languageOptions.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                            No languages match
                          </div>
                        ) : languageOptions.map((item) => {
                          const selected = normalizeChatLanguage(user?.preferredLanguage) === item.code;
                          return (
                            <button
                              key={item.code}
                              type="button"
                              role="option"
                              aria-selected={selected}
                              onClick={() => {
                                dispatch(updatePreferredLanguage(item.code));
                                setShowLanguageMenu(false);
                              }}
                              className={`w-full px-3 py-1.5 flex items-center justify-between gap-2 text-left text-xs ${
                                selected
                                  ? "bg-violet-50 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200"
                                  : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                              }`}
                            >
                              <span>{item.label}</span>
                              <span className="font-semibold tracking-wide text-slate-400">{item.symbol}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        {!isSuperAdmin && (
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Channels Section */}
            <div className={`flex flex-col min-h-0 ${channelsExpanded ? "flex-1" : "flex-shrink-0"}`}>
              <div className="p-2 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button onClick={() => setChannelsExpanded(!channelsExpanded)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded">
                      {channelsExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </button>
                    <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Channels</h2>
                  </div>
                  <button onClick={() => setIsCreateModalOpen(true)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"><PlusIcon /></button>
                </div>
                {channelsExpanded && (
                  <div className="mt-2">
                    <input
                      type="text"
                      placeholder="Search channels..."
                      value={channelSearchQuery}
                      onChange={(e) => setChannelSearchQuery(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                    />
                  </div>
                )}
              </div>

              {channelsExpanded && (
                <div className="flex-1 overflow-y-auto px-2 custom-scrollbar">
                  <div className="space-y-0.5 pb-2">
                    {getSortedChannels(channels)
                      .filter((c) => {
                        const raw = c.type;
                        const type = (
                          typeof raw === 'string'
                            ? raw
                            : (raw && typeof raw === 'object' ? (raw.name || raw.type || '') : String(raw || ''))
                        ).toString().toUpperCase();
                        if (type === 'DIRECT_MESSAGE' || type === 'DIRECT' || type === 'DM') return false;
                        if (isTaskCommentChannel(c)) return false;
                        if (!isVisibleSidebarChannel(c, user)) return false;
                        if (!channelSearchQuery) return true;
                        return c.name?.toLowerCase().includes(channelSearchQuery.toLowerCase());
                      })
                      .map(channel => {
                        const unreadCount = unreadCounts[channel.id] || 0;
                        const isSoundEnabled = soundPreferences[channel.id] !== undefined ? soundPreferences[channel.id] : true;
                        const isAttendance =
                          channel.type === 'ATTENDANCE'
                          || channel.systemChannelKey === 'ATTENDANCE'
                          || channel.name === 'Attendance';
                        const isNotifications =
                          channel.type === 'NOTIFICATIONS'
                          || channel.systemChannelKey === 'NOTIFICATIONS'
                          || channel.name === 'Notifications';
                        const isAnnouncement = channel.type === 'ANNOUNCEMENT' || channel.name === 'Announcements';
                        return (
                          <button
                            key={channel.id}
                            onClick={() => handleChannelClick(channel)}
                            className={`w-full text-left px-2 py-1.5 text-sm rounded-lg flex items-center justify-between group ${
                              currentChannel?.id === channel.id
                                ? isAttendance
                                  ? "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200"
                                  : isNotifications
                                  ? "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200"
                                  : "bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300"
                                : isAttendance
                                  ? "text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                                  : isNotifications
                                  ? "text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-950/40"
                                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            }`}
                          >
                            <div className="flex items-center space-x-2 truncate">
                              {channel.imageUrl ? (
                                <PresignedAvatar
                                  fileKey={channel.imageUrl}
                                  name={channel.name}
                                  initials={channel.name?.substring(0, 2).toUpperCase() || 'CH'}
                                  className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                                  fallbackClassName={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${
                                    isAttendance ? 'bg-emerald-600' : isNotifications ? 'bg-indigo-600' : 'bg-primary-500'
                                  }`}
                                />
                              ) : isAttendance ? (
                                <span className="flex-shrink-0 w-5 h-5 rounded-md bg-emerald-600 text-white text-[10px] font-bold flex items-center justify-center">A</span>
                              ) : isNotifications ? (
                                <span className="flex-shrink-0 w-5 h-5 rounded-md bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center">N</span>
                              ) : (
                                <span className={`flex-shrink-0 w-5 text-center ${isAnnouncement ? 'text-amber-500' : 'text-gray-400 dark:text-gray-500'}`}>#</span>
                              )}
                              <span className={`truncate ${isAttendance || isAnnouncement || isNotifications ? 'font-bold' : ''}`}>
                                {channel.name}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button onClick={(e) => handleToggleSound(channel.id, e)} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-opacity">
                                {isSoundEnabled ? <VolumeIcon /> : <VolumeMutedIcon />}
                              </button>
                              {unreadCount > 0 && (
                                <span className={`${isAttendance ? 'bg-emerald-600' : isNotifications ? 'bg-indigo-600' : 'bg-primary-500'} text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1`}>
                                  {unreadCount > 99 ? "99+" : unreadCount}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* Direct Messages Section */}
            <div className={`flex flex-col min-h-0 ${directMessagesExpanded ? "flex-1" : "flex-shrink-0"} border-t border-gray-200 dark:border-gray-700`}>
              <div className="p-2 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button onClick={() => setDirectMessagesExpanded(!directMessagesExpanded)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded">
                      {directMessagesExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </button>
                    <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Messages</h2>
                  </div>
                  <button onClick={() => setIsStartDMModalOpen(true)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"><PlusIcon /></button>
                </div>
                {directMessagesExpanded && (
                  <div className="mt-2">
                    <input
                      type="text"
                      placeholder="Search messages..."
                      value={dmSearchQuery}
                      onChange={(e) => setDmSearchQuery(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
                    />
                  </div>
                )}
              </div>

              {directMessagesExpanded && (
                <div className="flex-1 overflow-y-auto px-2 custom-scrollbar">
                  <div className="space-y-0.5 pb-2">
                    {visibleDirectMessages
                      .filter(dm => {
                        if (!dmSearchQuery.trim()) return true;
                        const query = dmSearchQuery.toLowerCase();
                        if (isWorkloopDm(dm)) {
                          const label = workloopInboxLabel(user, dm).toLowerCase();
                          return label.includes(query) || "workloop".includes(query) || "organization".includes(query);
                        }
                        const otherUser = findUserForDm(dm, user?.id, peopleForDms);
                        const displayName = (userDisplayName(otherUser) || dm.name || "").toLowerCase();
                        const username = String(otherUser?.username || "").toLowerCase();
                        return displayName.includes(query) || username.includes(query);
                      })
                      .map(dm => {
                        const workloopChannel = isWorkloopDm(dm);
                        const otherUser = findUserForDm(dm, user?.id, peopleForDms);
                        const workloopLabel = workloopInboxLabel(user, dm);
                        const displayName = workloopChannel
                          ? workloopLabel
                          : (userDisplayName(otherUser) || dm.name || "Unknown User");
                        const unreadCount = unreadCounts[dm.id] || 0;
                        const isSoundEnabled = soundPreferences[dm.id] !== undefined ? soundPreferences[dm.id] : true;

                        return (
                          <button
                            key={dm.id}
                            onClick={() => handleChannelClick(dm)}
                            className={`w-full text-left px-2 py-1.5 text-sm rounded-lg flex items-center justify-between group ${
                              currentChannel?.id === dm.id
                                ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            <div className="flex items-center space-x-2 min-w-0">
                              <div className="relative flex-shrink-0">
                                {workloopChannel ? (
                                  <div className="w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold">{workloopInboxInitial(workloopLabel)}</div>
                                ) : (
                                  <PresignedAvatar
                                    fileKey={otherUser?.profilePicture}
                                    name={displayName}
                                    initials={otherUser?.firstName?.[0] || displayName?.[0] || "U"}
                                    className="w-5 h-5 rounded-full object-cover"
                                    fallbackClassName="w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center text-white text-[10px] font-medium"
                                  />
                                )}
                                {!workloopChannel && (
                                  <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 border-2 border-white dark:border-gray-800 rounded-full ${presenceDotClass(otherUser)}`} />
                                )}
                              </div>
                              <span className="truncate">{displayName}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button onClick={(e) => handleToggleSound(dm.id, e)} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-opacity">
                                {isSoundEnabled ? <VolumeIcon /> : <VolumeMutedIcon />}
                              </button>
                              {unreadCount > 0 && (
                                <span className="bg-primary-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                                  {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setShowStatusModal(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Set Status</h2>
                <button onClick={() => setShowStatusModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded"><CloseIcon /></button>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {[
                  { value: 'WORK_FROM_OFFICE', label: 'Work from office', active: 'bg-sky-500 text-white shadow-md', idle: 'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-800' },
                  { value: 'WORK_FROM_HOME', label: 'Work from home', active: 'bg-emerald-500 text-white shadow-md', idle: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800' },
                ].map(({ value, label, active, idle }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      dispatch(updateUserStatus({ workLocation: value, source: 'MANUAL' }));
                      dispatch(fetchUsers());
                    }}
                    className={`rounded-2xl border px-3 py-3 text-center text-[13px] font-semibold leading-tight transition ${
                      resolveWorkLocation(user) === value ? active : idle
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="space-y-1 border-t border-gray-200 dark:border-gray-700 pt-2">
                {[
                  { value: 'AVAILABLE', color: 'bg-green-500' },
                  { value: 'AWAY', color: 'bg-yellow-500' },
                  { value: 'LONG_AWAY', color: 'bg-amber-500' },
                  { value: 'OFFLINE', color: 'bg-gray-400' },
                  { value: 'LOGGED_OUT', color: 'bg-slate-500' },
                  { value: 'ON_LEAVE', color: 'bg-indigo-500' },
                  { value: 'DO_NOT_DISTURB', color: 'bg-red-500' },
                  { value: 'SICK_LEAVE', color: 'bg-orange-500' },
                  { value: 'HOLIDAY', color: 'bg-blue-500' },
                  { value: 'HAVING_FOOD', color: 'bg-amber-500' },
                ].map(({ value, color }) => (
                  <button
                    key={value}
                    onClick={() => {
                      dispatch(updateUserStatus({ status: value, source: 'MANUAL' }));
                      dispatch(fetchUsers());
                      setShowStatusModal(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      (user?.status === value || (value === 'AVAILABLE' && ['ONLINE', 'AVAILABLE', 'WORK_FROM_HOME', 'WORK_FROM_OFFICE'].includes(user?.status)))
                        ? 'bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'
                    }`}
                  >
                    <span className={`w-3 h-3 rounded-full ${color}`} />
                    <span className="text-sm font-medium">{STATUS_LABELS[value] || value}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <CreateChannelModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onChannelCreated={handleChannelClick}
      />

      <CreateMessageTemplateModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
      />

      <CaptionSettingsModal
        open={isCaptionSettingsOpen}
        onClose={() => setIsCaptionSettingsOpen(false)}
      />

      <StartDirectMessageModal
        isOpen={isStartDMModalOpen}
        onClose={() => setIsStartDMModalOpen(false)}
        onDirectMessageCreated={(channel) => {
          handleChannelClick(channel);
          dispatch(fetchDirectMessages());
        }}
      />

      {showUserProfile && selectedUserId && (
        <UserProfileModal
          userId={selectedUserId}
          isOpen={showUserProfile}
          onClose={() => {
            setShowUserProfile(false);
            setSelectedUserId(null);
          }}
        />
      )}
    </div>
  );
};

export default Sidebar;
